<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { createEventDispatcher } from 'svelte';
	// Constants
	import { BonusLimits } from '@/Constants/Promotions.js';
	// Components
	import Datepicker from '@/Components/Datepicker.svelte';
	import Modal from '@/Components/Modal.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Switch from '@/Components/Switch.svelte';
	// Services
	import { notify } from '@/Services/Notify';
	// Stores
	import PromotionStore from '@/Stores/PromotionStore';

	export let type = false;
	export let open = false;

	const dispatch = createEventDispatcher();

	let form = {
		name: null,
		title: null,
		description: null,
		code: null,
		promotion_type_id: type,
		published_at: null,
		expired_at: null,
		rules: {
			percentage: null,
			max_bonus_amount: null,
			valid_days: 30
		}
	};

	let errors = {};

	let activeDate = dayjs(form.published_at ?? undefined).format('YYYY-MM-DD');
	let activeTime = dayjs(form.published_at ?? undefined).format('HH:mm');
	let expireDate = dayjs(form.expired_at ?? undefined).format('YYYY-MM-DD');
	let expireTime = dayjs(form.expired_at ?? undefined).format('HH:mm');

	let activeToggle = !!form.published_at;
	let expireToggle = !!form.expired_at;

	$: sending = false;
	$: activeStart = dayjs(activeDate + ' ' + activeTime + ':00');
	$: expireStart = dayjs(expireDate + ' ' + expireTime + ':00');
	$: activeStart && expireStart && updateDateTimes();

	function updateDateTimes() {
		form.published_at = dayjs(activeStart).format('YYYY-MM-DDTHH:mm:00Z');
		form.expired_at = dayjs(expireStart).format('YYYY-MM-DDTHH:mm:00Z');
	}

	function createPromotion() {
		if (form.rules.percentage > 100 && form.rules.max_bonus_amount > 1000000) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'The selected promotion is above the maximum allowed',
				timeout: 3
			});
			errors = {
				'rules.percentage': ['Combination with max bonus amount is above the maximum allowed'],
				'rules.max_bonus_amount': ['Combination with template type is above the maximum allowed']
			};
			return false;
		}
		PromotionStore.createPromotion(form)
			.then(() => {
				notify({
					type: 'success',
					title: 'Promotion created',
					message: 'Promotion successfully created!',
					timeout: 3
				});
				dispatch('created');
				open = false;
				form = {
					name: null,
					title: null,
					description: null,
					code: null,
					promotion_type_id: type,
					published_at: form.published_at,
					expired_at: form.expired_at,
					rules: {
						percentage: null,
						max_bonus_amount: null,
						valid_days: 30
					}
				};
				updateDateTimes();
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem creating promotion',
					timeout: 3
				});
			});
	}

	updateDateTimes();
</script>

{#if open}
	<Modal on:close={() => (open = false)}>
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">Create new next deposit promotion</h2>
		<div class="flex flex-col w-full px-4 pt-2 text-left space-y-4 mt-4">
			<div class="flex flex-row space-x-4">
				<TextInput
					label="Promotion Name"
					name="name"
					className="w-1/2"
					placeholder="Please enter the promotion name"
					errors={errors && errors.name}
					bind:value={form.name}
				/>
				<TextInput
					label="Promotion Title  ** CUSTOMER VISIBLE **"
					name="title"
					className="w-1/2"
					placeholder="Please enter the promotion title"
					errors={errors && errors.title}
					bind:value={form.title}
				/>
			</div>
			<TextInput
				label="Promotion Description  ** CUSTOMER VISIBLE **"
				name="description"
				className="w-full"
				placeholder="Please enter the promotion description"
				errors={errors && errors.description}
				bind:value={form.description}
			/>
			<div class="flex flex-row space-x-4">
				<SelectInput
					className="w-1/2"
					label="Select Template Type"
					name="template_type"
					errors={errors && errors['rules.percentage']}
					required={true}
					bind:value={form.rules.percentage}
				>
					<option value={null} selected disabled>Select template type...</option>
					<option value="10">10%</option>
					<option value="20">20%</option>
					<option value="25">25%</option>
					<option value="30">30%</option>
					<option value="40">40%</option>
					<option value="50">50%</option>
					<option value="60">60%</option>
					<option value="70">70%</option>
					<option value="75">75%</option>
					<option value="100">100%</option>
					<option value="120">120%</option>
					<option value="150">150%</option>
					<option value="200">200%</option>
				</SelectInput>
				<SelectInput
					className="w-1/2"
					label="Max Bonus Amount"
					name="max_bonus"
					errors={errors && errors['rules.max_bonus_amount']}
					required={true}
					bind:value={form.rules.max_bonus_amount}
				>
					<option value={null} selected disabled>Select Amount...</option>
					{#each BonusLimits as bonusLimit (bonusLimit.value)}
						<option value={bonusLimit.value}>{bonusLimit.label}</option>
					{/each}
				</SelectInput>
			</div>
			<div class="flex flex-row space-x-4">
				<TextInput
					label="Promotion Code"
					name="code"
					className="w-1/2"
					placeholder="Offer Code"
					errors={errors && errors.code}
					bind:value={form.code}
				/>
				<TextInput
					label="Bonus Valid for (in days)"
					name="valid_days"
					errors={errors && errors['rules.valid_days']}
					className="w-1/2"
					type="number"
					min="0"
					max="30"
					step="1"
					bind:value={form.rules.valid_days}
				/>
			</div>
			<div class="flex flex-row flex-shrink mt-4">
				<div class="flex flex-row items-center justify-center space-x-4">
					<div class="flex flex-row items-center justify-center mr-4">
						<label class="form-label" for="promo-activeTime">Publish At:</label>
						<Switch name={'promo-activeToggle'} bind:checked={activeToggle} className="ml-2" />
					</div>
					{#if activeToggle}
						<Datepicker
							selected={activeDate}
							displayFormat="DD-MM-YYYY"
							dateChosen={(date) => {
								activeDate = dayjs(date).format('YYYY-MM-DD');
							}}
							appendBtnStyle="h-10"
						/>
						<TextInput bind:value={activeTime} name="promo-activeTime" type="time" inputClasses="h-10" />
					{/if}
				</div>
			</div>
			<div class="flex flex-row flex-shrink mt-4 mb-4">
				<div class="flex flex-row items-center justify-center space-x-4">
					<div class="flex flex-row items-center justify-center mr-4">
						<label class="form-label" for="promo-expireTime">Expires At:</label>
						<Switch name={'promo-expireToggle'} bind:checked={expireToggle} className="ml-2" />
					</div>
					{#if expireToggle}
						<Datepicker
							selected={expireDate}
							displayFormat="DD-MM-YYYY"
							dateChosen={(date) => {
								expireDate = dayjs(date).format('YYYY-MM-DD');
							}}
							appendBtnStyle="h-10"
						/>
						<TextInput bind:value={expireTime} name="promo-expireTime" type="time" inputClasses="h-10" />
					{/if}
				</div>
			</div>
			<LoadingButton on:click={() => createPromotion()} loading={sending} className="mb-4 btn btn-primary"
				>Create Promotion
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}>Cancel</button>
		</div>
	</Modal>
{/if}
